import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import React, { ReactNode, useState } from 'react'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { DialogFooter } from '@/components/ui/dialog'
import { Modal, ModalProps } from '../Modal'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import ButtonLoading from '@/components/misc/Button'

interface IStepTabs {
  name: string
  icon?: ReactNode
  disabled?: boolean
  badge?: {
    text: string
    isAnimated?: boolean
    className?: string
  }
  actionButtons: {
    label: string
    secondaryAction?: () => void
    primaryAction?: (data: any) => void
  }[]
  component: ReactNode
}

interface IModalWizardTabsProps extends Omit<ModalProps, 'footer'> {
  title?: string
  subtitle?: string
  isOpen: boolean
  onClose: Function
  steps: IStepTabs[]
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  modalClassName?: string
  isSending: boolean
  showDialogAlert: boolean
  confirmOnClose?: boolean
  onTabChange?: (idx: number) => void // nueva prop
}

export function ModalWizardTabs({
  steps,
  isOpen,
  onClose,
  title,
  subtitle,
  size,
  modalClassName,
  isSending,
  showDialogAlert,
  confirmOnClose = false,
  onTabChange,
}: IModalWizardTabsProps) {
  const { useFormWizzard } = useModalWizardTabsContext()
  const [currentStep, setCurrentStep] = useState<IStepTabs>(steps[0])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      showDialogAlert={showDialogAlert}
      subtitle={subtitle}
      size={size}
      modalClassName={modalClassName}
      confirmOnClose={confirmOnClose}
    >
      <Tabs
        value={currentStep.name}
        onValueChange={(value) => {
          const idx = steps.findIndex((item) => item.name === value)
          setCurrentStep(steps[idx]!)
          onTabChange && onTabChange(idx)
        }}
        className="flex h-fit w-full flex-col"
      >
        <TabsList className="my-4 flex w-full">
          {steps.map((step, idx) => {
            return (
              <TabsTrigger
                disabled={step.disabled}
                key={idx}
                value={step.name}
                data-value={step.name}
                className="flex w-full items-center gap-1"
              >
                {step.icon}
                <span>{step.name}</span>
                {step.badge && (
                  <Badge
                    className={`absolute -right-2 -top-2 ${step.badge.isAnimated ? 'animate-[ping_0.5s_ease-out_forwards]' : ''} bg-red-500 ${step.badge.className}`}
                    aria-hidden="true"
                  >
                    {step.badge.text}
                  </Badge>
                )}
              </TabsTrigger>
            )
          })}
        </TabsList>
        <Form {...useFormWizzard}>
          <div
            className={'flex flex-col overflow-y-auto px-2 scrollbar-thin'}
          >
            {currentStep.component}
          </div>
          <DialogFooter className="flex-shrink-0 flex-col-reverse gap-2 border-t px-4 pt-4 sm:flex-row sm:justify-between sm:px-6">
            <Footer step={currentStep} isSending={isSending} />
          </DialogFooter>
        </Form>
      </Tabs>
    </Modal>
  )
}

interface IFooterProps {
  step: IStepTabs
  isSending: boolean
}

function Footer({ step, isSending }: IFooterProps) {
  const { useFormWizzard } = useModalWizardTabsContext()
  const { handleSubmit } = useFormWizzard
  return (
    <div className={'flex w-full justify-end gap-2'}>
      {step.actionButtons.map((btn, index) =>
        isSending ? (
          <ButtonLoading
            loading={isSending}
            name="Guardando..."
            key={btn.label + '-' + index}
          ></ButtonLoading>
        ) : (
          <Button
            key={btn.label + '-' + index}
            disabled={isSending}
            variant={
              index === step.actionButtons.length - 1 ? 'default' : 'outline'
            }
            onClick={
              btn.primaryAction
                ? handleSubmit(btn.primaryAction)
                : btn.secondaryAction
            }
          >
            {btn.label}
          </Button>
        )
      )}
    </div>
  )
}
